import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, inrExact } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Sparkles, MapPin, Users, UserCheck, QrCode, Send, IndianRupee, Search,
  UserPlus, Plus, Trash2, Wallet, TrendingUp, Camera, Printer, BarChart3,
} from "lucide-react";

const EXPENSE_CATEGORIES = ["Venue", "Catering", "Marketing", "Staff", "Other"];
const CAT_STYLE = {
  Venue: "bg-blue-50 text-blue-700 border-blue-200",
  Catering: "bg-amber-50 text-amber-700 border-amber-200",
  Marketing: "bg-violet-50 text-violet-700 border-violet-200",
  Staff: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Other: "bg-slate-100 text-slate-600 border-slate-200",
};
const totalSpend = (m) => (m.expenses || []).reduce((s, e) => s + Number(e.amount || 0), 0);

export default function Mela() {
  const { meta } = useAuth();
  const navigate = useNavigate();
  const [melas, setMelas] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({ name: "", venue: "", date: "", target_count: 100 });
  const [open, setOpen] = useState(false);
  const [inviteMela, setInviteMela] = useState(null);
  const [checkinMela, setCheckinMela] = useState(null);
  const [costMela, setCostMela] = useState(null);
  const [selected, setSelected] = useState([]);
  const [deleteMela, setDeleteMela] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    api.get("/melas").then((r) => setMelas(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
  };
  useEffect(() => { load(); }, []);

  const syncOpenDialogs = (list) => {
    if (checkinMela) setCheckinMela(list.find((m) => m.id === checkinMela.id) || null);
    if (costMela) setCostMela(list.find((m) => m.id === costMela.id) || null);
  };

  const create = async () => {
    if (!form.name || !form.date) return toast.error("Name and date required");
    try {
      await api.post("/melas", { ...form, target_count: Number(form.target_count) });
      toast.success("Mela created");
      setOpen(false);
      setForm({ name: "", venue: "", date: "", target_count: 100 });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const invite = async () => {
    await api.post(`/melas/${inviteMela.id}/invite`, { customer_ids: selected });
    toast.success(`${selected.length} customers invited`);
    setInviteMela(null); setSelected([]); load();
  };

  const removeMela = async () => {
    setDeleting(true);
    try {
      await api.delete(`/melas/${deleteMela.id}`);
      toast.success(`Deleted "${deleteMela.name}"`);
      setDeleteMela(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Loan Mela"
        subtitle="Plan events, invite leads, run the front desk and track spend."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/app/mela-roi")} data-testid="roi-report-btn"><BarChart3 size={16} className="mr-2" />ROI Report</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0A192F] hover:bg-[#0F4C81]" data-testid="create-mela-btn">
                <Sparkles size={16} className="mr-2" />Create Mela
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create Loan Mela</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2">
                <div><Label className="mb-1.5 block">Name</Label><Input data-testid="mela-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Venue</Label><Input data-testid="mela-venue" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="mb-1.5 block">Date</Label><Input type="date" data-testid="mela-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                  <div><Label className="mb-1.5 block">Target Count</Label><Input type="number" value={form.target_count} onChange={(e) => setForm({ ...form, target_count: e.target.value })} /></div>
                </div>
              </div>
              <DialogFooter><Button onClick={create} data-testid="save-mela-btn" className="bg-[#0A192F] hover:bg-[#0F4C81]">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {melas.map((m) => {
          const invited = m.invited?.length || 0;
          const attended = m.attendees?.length || 0;
          const rate = invited ? Math.round((attended / invited) * 100) : 0;
          const spend = totalSpend(m);
          const cpa = attended ? Math.round(spend / attended) : 0;
          return (
            <Card key={m.id} className="p-0 overflow-hidden border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`mela-card-${m.id}`}>
              <div className="bg-[#0A192F] px-6 py-5 text-white">
                <div className="flex items-start justify-between">
                  <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center"><Sparkles size={22} className="text-emerald-400" /></div>
                  <div className="flex items-center gap-2">
                    <div className="text-xs text-slate-300 font-medium tracking-wide">{m.date}</div>
                    <button
                      onClick={() => setDeleteMela(m)}
                      className="p-1.5 rounded-md text-slate-400 hover:text-rose-400 hover:bg-white/10 transition-colors"
                      title="Delete this mela"
                      data-testid={`mela-delete-${m.id}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
                <h3 className="font-bold text-lg mt-3" style={{ fontFamily: "Outfit, sans-serif" }}>{m.name}</h3>
                <div className="text-sm text-slate-300 flex items-center gap-1.5 mt-1"><MapPin size={13} />{m.venue || "Venue TBD"}</div>
              </div>

              <div className="px-6 py-5">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div><div className="font-mono font-bold text-slate-900 text-lg">{m.target_count}</div><div className="text-[11px] uppercase tracking-wider text-slate-400">Target</div></div>
                  <div className="border-x border-slate-100"><div className="font-mono font-bold text-[#0F4C81] text-lg">{invited}</div><div className="text-[11px] uppercase tracking-wider text-slate-400">Invited</div></div>
                  <div><div className="font-mono font-bold text-emerald-600 text-lg">{attended}</div><div className="text-[11px] uppercase tracking-wider text-slate-400">Attended</div></div>
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1">
                    <span className="uppercase tracking-wider">Attendance</span><span className="font-mono">{rate}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${rate}%` }} />
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5" data-testid={`mela-spend-${m.id}`}>
                  <div className="flex items-center gap-2 text-slate-500 text-xs"><Wallet size={14} /> Total Spent</div>
                  <div className="text-right">
                    <div className="font-mono font-bold text-slate-900 text-sm">{inrExact(spend)}</div>
                    {attended > 0 && <div className="text-[10px] text-slate-400">{inrExact(cpa)} / attendee</div>}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mt-4">
                  <Button size="sm" variant="outline" onClick={() => { setInviteMela(m); setSelected([]); }} data-testid={`invite-btn-${m.id}`}><Send size={14} className="mr-1" />Invite</Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setCheckinMela(m)} data-testid={`checkin-btn-${m.id}`}><QrCode size={14} className="mr-1" />Desk</Button>
                  <Button size="sm" variant="outline" onClick={() => setCostMela(m)} data-testid={`cost-btn-${m.id}`}><IndianRupee size={14} className="mr-1" />Costs</Button>
                </div>
              </div>
            </Card>
          );
        })}
        {melas.length === 0 && <div className="text-slate-400">No melas yet. Create one to get started.</div>}
      </div>

      <AlertDialog open={!!deleteMela} onOpenChange={(o) => !o && setDeleteMela(null)}>
        <AlertDialogContent data-testid="mela-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this mela?</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently delete <b>{deleteMela?.name}</b>{deleteMela?.date ? ` (${deleteMela.date})` : ""}. This removes the event, its invite list, attendance and expense records. Customer profiles are not deleted. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); removeMela(); }} disabled={deleting} className="bg-rose-600 hover:bg-rose-700" data-testid="mela-delete-confirm-btn">
              {deleting ? "Deleting…" : "Delete mela"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Invite dialog */}
      <Dialog open={!!inviteMela} onOpenChange={(o) => !o && setInviteMela(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Users size={18} />Bulk Invite · {inviteMela?.name}</DialogTitle></DialogHeader>
          <label className="flex items-center gap-3 px-2 py-2 border-b bg-slate-50 rounded-t text-sm font-medium cursor-pointer" data-testid="invite-select-all">
            <input
              type="checkbox"
              checked={customers.length > 0 && selected.length === customers.length}
              ref={(el) => { if (el) el.indeterminate = selected.length > 0 && selected.length < customers.length; }}
              onChange={(e) => setSelected(e.target.checked ? customers.map((c) => c.id) : [])}
            />
            <span className="flex-1">Select all</span>
            <span className="text-xs text-slate-400">{selected.length}/{customers.length} selected</span>
          </label>
          <div className="max-h-80 overflow-y-auto space-y-1 pt-2">
            {customers.map((c) => (
              <label key={c.id} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50 cursor-pointer text-sm" data-testid={`invite-cust-${c.id}`}>
                <input type="checkbox" checked={selected.includes(c.id)} onChange={(e) => setSelected((p) => e.target.checked ? [...p, c.id] : p.filter((x) => x !== c.id))} />
                <span className="flex-1">{c.business_name}</span><span className="text-xs text-slate-400">{c.mobile}</span>
              </label>
            ))}
          </div>
          <DialogFooter><Button onClick={invite} disabled={!selected.length} data-testid="confirm-invite-btn" className="bg-[#0A192F] hover:bg-[#0F4C81]">Invite {selected.length} Customers</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Front Desk / Check-in dialog */}
      {checkinMela && (
        <CheckinDialog
          mela={checkinMela}
          customers={customers}
          meta={meta}
          onClose={() => setCheckinMela(null)}
          onChanged={() => { api.get("/melas").then((r) => { setMelas(r.data); syncOpenDialogs(r.data); }); api.get("/customers").then((r) => setCustomers(r.data)); }}
        />
      )}

      {/* Costs dialog */}
      {costMela && (
        <CostsDialog
          mela={costMela}
          onClose={() => setCostMela(null)}
          onChanged={() => { api.get("/melas").then((r) => { setMelas(r.data); syncOpenDialogs(r.data); }); }}
        />
      )}
    </div>
  );
}

function CheckinDialog({ mela, customers, meta, onClose, onChanged }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [invitedOnly, setInvitedOnly] = useState(true);
  const [walkOpen, setWalkOpen] = useState(false);
  const [walk, setWalk] = useState({ business_name: "", contact_person: "", mobile: "", loan_category: "" });
  const [saving, setSaving] = useState(false);
  const [scanCode, setScanCode] = useState("");
  const [scanning, setScanning] = useState(false);

  const submitCode = async (codeArg) => {
    const code = (typeof codeArg === "string" ? codeArg : scanCode).trim();
    if (!code) return;
    try {
      const { data } = await api.post(`/melas/${mela.id}/checkin-by-code`, { code });
      toast.success(`${data.customer.business_name} checked in`);
      setScanCode("");
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => {
    if (!scanning) return;
    let scanner;
    import("html5-qrcode").then(({ Html5Qrcode }) => {
      scanner = new Html5Qrcode("qr-reader");
      scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: 200 },
        (decoded) => { submitCode(decoded); setScanning(false); }, () => {})
        .catch(() => toast.error("Camera unavailable — use manual code entry"));
    });
    return () => { if (scanner) scanner.stop().catch(() => {}); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning]);

  const invitedSet = new Set(mela.invited || []);
  const presentSet = new Set(mela.attendees || []);

  const rows = customers.filter((c) => {
    if (invitedOnly && !invitedSet.has(c.id)) return false;
    if (query) {
      const q = query.toLowerCase();
      if (!(`${c.business_name} ${c.mobile} ${c.contact_person || ""}`.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  const doCheckin = async (cid) => {
    await api.post(`/melas/${mela.id}/checkin/${cid}`).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    onChanged();
  };

  const addWalkin = async () => {
    if (!walk.business_name.trim() || !walk.mobile.trim()) return toast.error("Name and mobile are required");
    setSaving(true);
    try {
      const { data } = await api.post(`/melas/${mela.id}/walkin`, walk);
      toast.success(data.already_existed ? "Existing customer checked in" : "Walk-in added & checked in");
      setWalk({ business_name: "", contact_person: "", mobile: "", loan_category: "" });
      setWalkOpen(false);
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserCheck size={18} />Front Desk · {mela.name}</DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-3 text-slate-500">
            <span><b className="text-[#0F4C81] font-mono">{invitedSet.size}</b> invited</span>
            <span><b className="text-emerald-600 font-mono">{presentSet.size}</b> present</span>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate(`/app/mela/${mela.id}/badges`)} data-testid="print-badges-btn"><Printer size={14} className="mr-1" />Badges</Button>
          <Button size="sm" variant="outline" onClick={() => setWalkOpen((v) => !v)} data-testid="walkin-toggle-btn"><UserPlus size={14} className="mr-1" />Add Walk-in</Button>
        </div>

        <div className="flex gap-2">
          <div className="relative flex-1">
            <QrCode size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Scan or type code / mobile, press Enter" data-testid="checkin-code-input"
              value={scanCode} onChange={(e) => setScanCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitCode(); }} />
          </div>
          <Button size="sm" variant="outline" onClick={() => submitCode()} data-testid="checkin-code-btn">Check in</Button>
          <Button size="sm" variant="outline" onClick={() => setScanning((v) => !v)} data-testid="scan-camera-btn"><Camera size={15} /></Button>
        </div>
        {scanning && <div id="qr-reader" data-testid="qr-reader" className="rounded-lg overflow-hidden border w-full" />}

        {walkOpen && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="walkin-form">
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Business / Name *" data-testid="walkin-name" value={walk.business_name} onChange={(e) => setWalk({ ...walk, business_name: e.target.value })} />
              <Input placeholder="Mobile *" data-testid="walkin-mobile" value={walk.mobile} onChange={(e) => setWalk({ ...walk, mobile: e.target.value })} />
              <Input placeholder="Contact person" data-testid="walkin-contact" value={walk.contact_person} onChange={(e) => setWalk({ ...walk, contact_person: e.target.value })} />
              <Select value={walk.loan_category} onValueChange={(v) => setWalk({ ...walk, loan_category: v })}>
                <SelectTrigger data-testid="walkin-loan-category"><SelectValue placeholder="Loan category" /></SelectTrigger>
                <SelectContent>{(meta?.loan_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={saving} onClick={addWalkin} data-testid="walkin-save-btn">
              {saving ? "Saving…" : "Add & Check in"}
            </Button>
          </div>
        )}

        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Search by name or mobile…" data-testid="checkin-search" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={invitedOnly} onCheckedChange={setInvitedOnly} data-testid="checkin-invited-only" />
          <Label className="text-sm text-slate-600">Show invited leads only</Label>
        </div>

        <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 border rounded-lg">
          {rows.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-400">No matching leads.</div>
          ) : rows.map((c) => {
            const present = presentSet.has(c.id);
            return (
              <div key={c.id} className="flex items-center gap-3 px-3 py-2.5 text-sm" data-testid={`checkin-cust-${c.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-800 truncate">{c.business_name}</div>
                  <div className="text-xs text-slate-400 font-mono">{c.mobile}</div>
                </div>
                {present
                  ? <span className="text-xs text-emerald-600 font-semibold flex items-center gap-1"><UserCheck size={13} />Present</span>
                  : <Button size="sm" variant="outline" onClick={() => doCheckin(c.id)} data-testid={`do-checkin-${c.id}`}>Check in</Button>}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CostsDialog({ mela, onClose, onChanged }) {
  const [exp, setExp] = useState({ item: "", amount: "", category: "Other", note: "" });
  const [saving, setSaving] = useState(false);
  const expenses = mela.expenses || [];
  const total = totalSpend(mela);
  const attended = mela.attendees?.length || 0;

  const add = async () => {
    if (!exp.item.trim() || !exp.amount) return toast.error("Item and amount are required");
    setSaving(true);
    try {
      await api.post(`/melas/${mela.id}/expenses`, { ...exp, amount: Number(exp.amount) });
      toast.success("Expense added");
      setExp({ item: "", amount: "", category: "Other", note: "" });
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (eid) => { await api.delete(`/melas/${mela.id}/expenses/${eid}`); onChanged(); };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Wallet size={18} />Mela Costs · {mela.name}</DialogTitle></DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-[#0A192F] text-white px-4 py-3" data-testid="cost-total">
            <div className="text-[11px] uppercase tracking-wider text-slate-300">Total Spent</div>
            <div className="font-mono font-bold text-xl">{inrExact(total)}</div>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 flex items-center gap-1"><TrendingUp size={12} />Cost / Attendee</div>
            <div className="font-mono font-bold text-xl text-slate-900">{attended ? inrExact(Math.round(total / attended)) : "—"}</div>
          </div>
        </div>

        <div className="max-h-56 overflow-y-auto divide-y divide-slate-100 border rounded-lg">
          {expenses.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-400">No expenses logged yet.</div>
          ) : expenses.map((e) => (
            <div key={e.id} className="flex items-center gap-3 px-3 py-2.5 text-sm group" data-testid={`expense-row-${e.id}`}>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium shrink-0 ${CAT_STYLE[e.category] || CAT_STYLE.Other}`}>{e.category}</span>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800 truncate">{e.item}</div>
                {e.note && <div className="text-xs text-slate-400 truncate">{e.note}</div>}
              </div>
              <div className="font-mono font-semibold text-slate-900">{inrExact(e.amount)}</div>
              <button onClick={() => remove(e.id)} data-testid={`expense-delete-${e.id}`} className="p-1.5 rounded-md text-slate-300 hover:text-rose-500 hover:bg-rose-50 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1"><Plus size={13} />Add Expense</div>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Item (e.g. Venue rent)" data-testid="expense-item" value={exp.item} onChange={(e) => setExp({ ...exp, item: e.target.value })} />
            <Input type="number" placeholder="Amount ₹" data-testid="expense-amount" value={exp.amount} onChange={(e) => setExp({ ...exp, amount: e.target.value })} />
            <Select value={exp.category} onValueChange={(v) => setExp({ ...exp, category: v })}>
              <SelectTrigger data-testid="expense-category"><SelectValue /></SelectTrigger>
              <SelectContent>{EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Note (optional)" data-testid="expense-note" value={exp.note} onChange={(e) => setExp({ ...exp, note: e.target.value })} />
          </div>
          <Button size="sm" className="w-full bg-[#0A192F] hover:bg-[#0F4C81]" disabled={saving} onClick={add} data-testid="expense-add-btn">{saving ? "Adding…" : "Add Expense"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
